import { apiClient } from "@/config/api";
import {
  Director,
  CreateDirectorDto,
  UpdateDirectorDto,
  GetPaginationDirectorsDto,
  DirectorListResponse,
  ExternalDirector,
} from "@/types/director";
import { ApiResponse } from "@/types/status";

const DIRECTOR_ENDPOINTS = {
  CREATE: "/directors",
  CREATE_WITH_IMAGE: "/directors/upload",
  GET_ALL: "/directors",
  GET_BY_ID: (id: string) => `/directors/${id}`,
  GET_BY_SLUG: (slug: string) => `/directors/slug/${slug}`,
  UPDATE: (id: string) => `/directors/${id}`,
  UPDATE_WITH_IMAGE: (id: string) => `/directors/${id}/upload`,
  DELETE: (id: string) => `/directors/${id}`,
  SYNC_BULK: "/directors/sync/bulk",
  SYNC_SINGLE: (externalId: string) => `/directors/sync/${externalId}`,
  GET_EXTERNAL: "/directors/external",
  GET_COUNTRIES: "/external/countries",
} as const;

export const directorService = {
  // Tạo đạo diễn mới
  create: async (data: CreateDirectorDto): Promise<ApiResponse<Director>> => {
    return await apiClient.post(DIRECTOR_ENDPOINTS.CREATE, data);
  },

  // Lấy danh sách đạo diễn với phân trang
  getPaginationDirectors: async (
    params: GetPaginationDirectorsDto
  ): Promise<ApiResponse<DirectorListResponse>> => {
    return await apiClient.get(DIRECTOR_ENDPOINTS.GET_ALL, params as any);
  },

  // Lấy tất cả đạo diễn
  getAllDirectors: async (): Promise<ApiResponse<Director[]>> => {
    return await apiClient.get(DIRECTOR_ENDPOINTS.GET_ALL);
  },

  // Lấy đạo diễn theo ID
  getDirectorById: async (id: string): Promise<ApiResponse<Director>> => {
    return await apiClient.get(DIRECTOR_ENDPOINTS.GET_BY_ID(id));
  },

  // Lấy đạo diễn theo slug
  getDirectorBySlug: async (slug: string): Promise<ApiResponse<Director>> => {
    return await apiClient.get(DIRECTOR_ENDPOINTS.GET_BY_SLUG(slug));
  },

  // Cập nhật đạo diễn (SEO fields)
  updateDirector: async (
    id: string,
    data: UpdateDirectorDto
  ): Promise<ApiResponse<Director>> => {
    return await apiClient.put(DIRECTOR_ENDPOINTS.UPDATE(id), data);
  },

  // Xóa đạo diễn
  deleteDirector: async (id: string): Promise<ApiResponse<void>> => {
    return await apiClient.delete(DIRECTOR_ENDPOINTS.DELETE(id));
  },

  // Sync tất cả đạo diễn từ external DB
  syncBulk: async (): Promise<
    ApiResponse<{ totalProcessed: number; created: number; updated: number }>
  > => {
    return await apiClient.post(DIRECTOR_ENDPOINTS.SYNC_BULK);
  },

  // Sync một đạo diễn từ external DB
  syncSingle: async (externalId: string): Promise<ApiResponse<Director>> => {
    return await apiClient.post(DIRECTOR_ENDPOINTS.SYNC_SINGLE(externalId));
  },

  // Lấy danh sách đạo diễn từ external DB
  getExternalDirectors: async (): Promise<ApiResponse<ExternalDirector[]>> => {
    return await apiClient.get(DIRECTOR_ENDPOINTS.GET_EXTERNAL);
  },

  // Tạo đạo diễn với upload hình ảnh
  createWithImage: async (
    data: CreateDirectorDto,
    imageFile?: File
  ): Promise<ApiResponse<Director>> => {
    const formData = new FormData();

    // Required fields
    formData.append("name", data.name);
    formData.append("url", data.url);

    // Optional fields
    if (data.description) formData.append("description", data.description);
    if (data.birthday) formData.append("birthday", data.birthday);
    if (data.countryName) formData.append("countryName", data.countryName);
    if (data.height !== undefined && data.height !== null)
      formData.append("height", String(data.height));
    if (data.seoTitle) formData.append("seoTitle", data.seoTitle);
    if (data.metaDescription)
      formData.append("metaDescription", data.metaDescription);

    // Image file or URL
    if (imageFile) {
      formData.append("image", imageFile);
    } else if (data.image_url) {
      formData.append("image_url", data.image_url);
    }

    return await apiClient.post(DIRECTOR_ENDPOINTS.CREATE_WITH_IMAGE, formData);
  },

  // Cập nhật đạo diễn với upload hình ảnh
  updateWithImage: async (
    id: string,
    data: UpdateDirectorDto,
    imageFile?: File
  ): Promise<ApiResponse<Director>> => {
    const formData = new FormData();

    // Add all provided fields
    if (data.name) formData.append("name", data.name);
    if (data.url) formData.append("url", data.url);
    if (data.description !== undefined)
      formData.append("description", data.description);
    if (data.birthday !== undefined) formData.append("birthday", data.birthday);
    if (data.countryName !== undefined)
      formData.append("countryName", data.countryName);
    if (data.height !== undefined && data.height !== null)
      formData.append("height", String(data.height));
    if (data.seoTitle !== undefined) formData.append("seoTitle", data.seoTitle);
    if (data.metaDescription !== undefined)
      formData.append("metaDescription", data.metaDescription);

    // Image file or URL
    if (imageFile) {
      formData.append("image", imageFile);
    } else if (data.image_url !== undefined) {
      formData.append("image_url", data.image_url);
    }

    return await apiClient.put(
      DIRECTOR_ENDPOINTS.UPDATE_WITH_IMAGE(id),
      formData
    );
  },

  // Lấy danh sách quốc gia
  getCountries: async (): Promise<
    ApiResponse<{ _id: string; name: string }[]>
  > => {
    return await apiClient.get(DIRECTOR_ENDPOINTS.GET_COUNTRIES);
  },
};
